/**
 * Team Management API
 * CRUD for team members and role/permission management
 */

import { apiClient } from './aiCrmApi';

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  companyRoleCode: string;
  companyRoleName: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface TeamRole {
  id: string;
  code: string;
  name: string;
  description: string;
  isSystem: boolean;
  dataScope: 'OWN' | 'ALL';
  permissions: string[];
  memberCount: number;
}

export interface CreateMemberInput {
  email: string;
  name: string;
  password: string;
  roleCode: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const teamApi = {
  // ---- Members ----

  getMembers: async (): Promise<TeamMember[]> => {
    const response = await apiClient.get<ApiResponse<TeamMember[]>>('/team/members');
    return response.data.data;
  },

  createMember: async (data: CreateMemberInput): Promise<TeamMember> => {
    const response = await apiClient.post<ApiResponse<TeamMember>>('/team/members', data);
    return response.data.data;
  },

  changeRole: async (userId: string, roleCode: string): Promise<TeamMember> => {
    const response = await apiClient.put<ApiResponse<TeamMember>>(
      `/team/members/${userId}/role`,
      { roleCode }
    );
    return response.data.data;
  },

  deactivateMember: async (userId: string): Promise<TeamMember> => {
    const response = await apiClient.put<ApiResponse<TeamMember>>(
      `/team/members/${userId}/deactivate`
    );
    return response.data.data;
  },

  reactivateMember: async (userId: string): Promise<TeamMember> => {
    const response = await apiClient.put<ApiResponse<TeamMember>>(
      `/team/members/${userId}/reactivate`
    );
    return response.data.data;
  },

  // ---- Roles ----

  getRoles: async (): Promise<TeamRole[]> => {
    const response = await apiClient.get<ApiResponse<TeamRole[]>>('/team/roles');
    return response.data.data;
  },

  updateRolePermissions: async (
    roleId: string,
    data: { permissions: string[]; dataScope: string }
  ): Promise<TeamRole> => {
    const response = await apiClient.put<ApiResponse<TeamRole>>(
      `/team/roles/${roleId}/permissions`,
      data
    );
    return response.data.data;
  },
};
